import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { io } from "socket.io-client";
import { useAuth } from "./AuthContext";

const SocketContext = createContext(null);

export const useSocket = () => useContext(SocketContext);

export const SocketProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [socket, setSocket] = useState(null);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    if (isAuthenticated && user) {
      // Connect to socket server
      const newSocket = io(import.meta.env.VITE_API_URL?.replace("/api", "") || "http://localhost:3000", {
        auth: { token: localStorage.getItem("accessToken") },
        transports: ["websocket", "polling"],
      });

      newSocket.on("connect", () => {
        console.log("🔌 Socket connected");
        setIsConnected(true);
        newSocket.emit("go_online");
      });

      newSocket.on("disconnect", () => {
        console.log("🔌 Socket disconnected");
        setIsConnected(false);
      });

      newSocket.on("users_online", (users) => {
        setOnlineUsers(users);
      });

      newSocket.on("user_online", (userId) => {
        setOnlineUsers((prev) => [...prev, userId]);
      });

      newSocket.on("user_offline", (userId) => {
        setOnlineUsers((prev) => prev.filter((id) => id !== userId));
      });

      setSocket(newSocket);

      return () => {
        newSocket.emit("go_offline");
        newSocket.disconnect();
      };
    }
  }, [isAuthenticated, user?._id]);

  // Send message
  const sendMessage = useCallback((data) => {
    if (socket && isConnected) {
      socket.emit("send_message", data);
    }
  }, [socket, isConnected]);

  // Join room
  const joinRoom = useCallback((roomId) => {
    if (socket && isConnected) {
      socket.emit("join_room", roomId);
    }
  }, [socket, isConnected]);

  // Leave room
  const leaveRoom = useCallback((roomId) => {
    if (socket && isConnected) {
      socket.emit("leave_room", roomId);
    }
  }, [socket, isConnected]);

  // Send typing indicator
  const sendTyping = useCallback((data) => {
    if (socket && isConnected) {
      socket.emit("typing", data);
    }
  }, [socket, isConnected]);

  // Mark message as read
  const markRead = useCallback((data) => {
    if (socket && isConnected) {
      socket.emit("message_read", data);
    }
  }, [socket, isConnected]);

  // Listen for events
  const on = useCallback((event, callback) => {
    if (socket) {
      socket.on(event, callback);
    }
  }, [socket]);

  const off = useCallback((event, callback) => {
    if (socket) {
      socket.off(event, callback);
    }
  }, [socket]);

  // Check if user is online
  const isOnline = useCallback((userId) => {
    return onlineUsers.includes(userId);
  }, [onlineUsers]);

  return (
    <SocketContext.Provider
      value={{
        socket,
        isConnected,
        onlineUsers,
        sendMessage,
        joinRoom,
        leaveRoom,
        sendTyping,
        markRead,
        on,
        off,
        isOnline,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
};

export default SocketProvider;